import React, { useCallback, useRef, useState } from 'react';
import { useTrans } from '../../../hooks/useTrans';

export default function FileDropzone({ onChange, accept = 'text/csv' }) {
    const translate = useTrans();
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [message, setMessage] = useState(translate('Plaats bestand hier'));

    const readCsvLineCount = useCallback((file) => {
        const reader = new FileReader();
        reader.addEventListener('load', (event) => {
            const rawData = event.target.result;
            if (rawData && rawData.includes(',')) {
                const data = atob(rawData.split(',')[1]);
                const lineCount = (data.match(/\n/g) || []).length;
                setMessage(`${file.name} - ${translate('bevat')} ${lineCount} ${translate('regels')}`);
            } else {
                setMessage(file.name);
            }
        });
        reader.readAsDataURL(file);
    }, [translate]);

    function handleFile(file) {
        if (!file) {
            setMessage(translate('Plaats bestand hier'));
            onChange(null);
            return;
        }

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            setMessage(translate('Alleen CSV toegestaan'));
            onChange(null);
            return;
        }

        readCsvLineCount(file);
        onChange(file);
    }

    function handleDragEvents(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    function handleDragEnter(e) {
        handleDragEvents(e);
        setIsDragging(true);
    }

    function handleDragLeave(e) {
        handleDragEvents(e);
        setIsDragging(false);
    }

    function handleDrop(e) {
        handleDragEvents(e);
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length !== 1) {
            setMessage(translate('Upload een enkel CSV-bestand'));
            onChange(null);
            return;
        }

        handleFile(files[0]);

        // Sync with input element
        if (inputRef.current) {
            inputRef.current.files = files;
        }
    }

    function handleInputChange(e) {
        handleFile(e.target.files?.[0] || null);
    }

    return (
        <div
            onClick={() => inputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragEnd={handleDragLeave}
            onDrop={handleDrop}
            className={`flex min-h-[80px] cursor-pointer items-center justify-center rounded-sm border-2 border-dashed transition-colors ${
                isDragging
                    ? 'border-[#0e8e6a] bg-[#0e8e6a]/5'
                    : 'border-[#bbb] hover:border-[#003545]/40'
            }`}
        >
            <span className="text-sm text-[#808080] select-none">{message}</span>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handleInputChange}
                className="hidden"
            />
        </div>
    );
}
